import { useEffect, useMemo, useState } from "react";
import { cockpitService } from "../../../../Core/Api/services";
import { WorkflowType } from "../../../../Core/Api/Types/workflow-type";
import KpiWorkflowTable from "../../../../Components/Table/KpiWorkflowTable/kpi-workflow-table";
import { KpiPersonal } from "../../../../Core/Api/Types/get-workflow-user-tasks-kpi-response";
import TableSkeleton from "../../../../Components/Table/TableSkeleton";
import {
  Autocomplete,
  Box,
  Divider,
  Grid,
  OutlinedInput,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import KpiCharts from "./kpi-charts";
import dayjs from "dayjs";
import TableRowsIcon from "@mui/icons-material/TableRows";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import TocIcon from "@mui/icons-material/Toc";
import EqualizerIcon from "@mui/icons-material/Equalizer";
import VerticalSplitIcon from "@mui/icons-material/VerticalSplit";
import HorizontalSplitIcon from "@mui/icons-material/HorizontalSplit";
import { ObjectType } from "../../../../Core/Api/Types/object-type";
import AssetIcon from "../../../../Components/Misc/AssetIcon/asset-icon";
import { useTranslation } from "react-i18next";

var moment = require("moment");
require("moment-duration-format");

export function timespanToSec(value: string | null) {
  if (!value) return null;
  const splitted = value.split(".");
  if (splitted.length < 2) return null;
  const splittedHHmmss = splitted[splitted.length === 2 ? 0 : 1].split(":");
  if (splittedHHmmss.length < 3) return null;
  let dayToSec = splitted.length === 2 ? 0 : parseInt(splitted[0]) * 3600 * 24;
  let HHToSec = parseInt(splittedHHmmss[0]) * 3600;
  let minToSec = parseInt(splittedHHmmss[1]) * 60;
  let sec = parseInt(splittedHHmmss[2]);

  if (!(dayToSec > -1) || !(HHToSec > -1) || !(minToSec > -1) || !(sec > -1)) return null;

  return dayToSec + HHToSec + minToSec + sec;
}

function KpiWorkflow() {
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState<string>(moment(new Date()).add(-1, "M").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState<string>(moment(new Date()).format("YYYY-MM-DD"));
  const [workflowType, setWorkflowType] = useState<WorkflowType>(WorkflowType.Ingest);
  const [kpiData, setKpiData] = useState<KpiPersonal[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [stackDirection, setStackDirection] = useState<"row" | "column">("column");
  const [displayMode, setDisplayMode] = useState<"table" | "graph" | "split">("split");
  const [selectedRow, setSelectedRow] = useState<number>(-1);
  const theme = useTheme();

  useEffect(() => {
    setIsLoading(true);
    setSelectedRow(-1);
    cockpitService.GetKpiWorkflowData(workflowType, startDate, endDate).then((data) => {
      setIsLoading(false);
      if (data.kpi) {
        setKpiData(data.kpi);
      }
      if (data.kpi && data.kpi[0]) {
        setSelectedRow(data.kpi[0].userId!);
      }
    });
  }, [workflowType, startDate, endDate]);

  const currentUserIndex = useMemo(
    () => (kpiData.length > 0 ? kpiData.findIndex((x) => x.userId === selectedRow) : -1),
    [selectedRow, kpiData]
  );

  const currentUserData = useMemo(
    () => (kpiData.length - 1 >= currentUserIndex ? kpiData[currentUserIndex] : null),
    [currentUserIndex, kpiData]
  );

  const parsedUserData = useMemo(() => {
    if (!currentUserData) return null;

    let objectTypeMap = Object.values(ObjectType).map((x) => ({
      objectType: x,
      count: 0,
      spentTimeTotal: 0,
      averageDelayTime: 0,
      averageTaskTime: 0,
    }));

    for (let i = 0; i < currentUserData.counters.length; i++) {
      currentUserData.counters[i].counters.forEach((x) => {
        objectTypeMap = objectTypeMap.map((o) => {
          if (o.objectType === x.objectType && x.count) {
            let averageDelayTimeInSec = timespanToSec(x.averageDelayTime);
            let newAverageDelayTime = averageDelayTimeInSec
              ? (averageDelayTimeInSec * x.count + o.averageDelayTime * o.count) / (x.count + o.count)
              : o.averageDelayTime;

            let spentTimeTotalInSec = timespanToSec(x.spentTimeTotal);
            let newSpentTimeTotal = spentTimeTotalInSec ? spentTimeTotalInSec + o.spentTimeTotal : o.spentTimeTotal;

            let averageTaskTimeInSec = timespanToSec(x.averageTaskTime);
            let newAverageTaskTime = averageTaskTimeInSec
              ? (averageTaskTimeInSec * x.count + o.averageTaskTime * o.count) / (x.count + o.count)
              : o.averageTaskTime;
            return {
              ...o,
              count: x.count ? o.count + x.count : o.count,
              averageDelayTime: newAverageDelayTime,
              spentTimeTotal: newSpentTimeTotal,
              averageTaskTime: newAverageTaskTime,
            };
          } else return o;
        });
      });
    }
    return objectTypeMap.filter((x) => x.count > 0);
  }, [currentUserData]);

  const generateDataBlock = (
    objectTypeData: {
      objectType: ObjectType;
      count: number;
      spentTimeTotal: number;
      averageDelayTime: number;
      averageTaskTime: number;
    },
    isLast: boolean,
    index: number
  ) => (
    <Box key={index} display={"flex"} flexDirection={"column"}>
      <Stack ml={1} direction={"row"}>
        <AssetIcon sx={{ position: "relative", bottom: "2px" }} colored type={objectTypeData.objectType} />
        <Typography pl={1.5} mb={0.4} width={"100%"} variant="h6">
          {objectTypeData.objectType}
        </Typography>
      </Stack>
      <Divider sx={{ m: 1, mt: 0.5 }} orientation="horizontal" />
      <Grid justifyContent={"space-around"} direction={"row"} width={"100%"} spacing={1} container>
        <Grid
          minWidth={"150px"}
          justifyContent={"space-between"}
          display={"flex"}
          flexDirection={"row"}
          ml={1.5}
          mr={1.5}
          item
          md={displayMode === "split" && stackDirection === "row" ? 12 : 5}
          sm={12}
        >
          <Typography textAlign={"left"} variant="body2" fontWeight={500} color="text.tier">
            {t("page:body.averageTaskTime")}
          </Typography>
          <Typography textAlign={"right"} variant="body2" fontWeight={500}>
            {moment.duration(objectTypeData.averageTaskTime, "seconds")?.format()}
          </Typography>
        </Grid>
        <Grid
          minWidth={"150px"}
          justifyContent={"space-between"}
          display={"flex"}
          flexDirection={"row"}
          ml={1.5}
          mr={1.5}
          item
          md={displayMode === "split" && stackDirection === "row" ? 12 : 5}
          sm={12}
        >
          <Typography textAlign={"left"} variant="body2" fontWeight={500} color="text.tier">
            {t("page:body.averageDelayTime")}
          </Typography>
          <Typography textAlign={"right"} variant="body2" fontWeight={500}>
            {moment.duration(objectTypeData.averageDelayTime, "seconds")?.format()}
          </Typography>
        </Grid>
        <Grid
          minWidth={"150px"}
          justifyContent={"space-between"}
          display={"flex"}
          flexDirection={"row"}
          ml={1.5}
          mr={1.5}
          item
          md={displayMode === "split" && stackDirection === "row" ? 12 : 5}
          sm={12}
        >
          <Typography textAlign={"left"} variant="body2" fontWeight={500} color="text.tier">
            {t("page:body.totalTimeSpent")}
          </Typography>
          <Typography textAlign={"right"} variant="body2" fontWeight={500}>
            {moment.duration(objectTypeData.spentTimeTotal, "seconds")?.format()}
          </Typography>
        </Grid>
        <Grid
          minWidth={"150px"}
          justifyContent={"space-between"}
          display={"flex"}
          flexDirection={"row"}
          ml={1.5}
          mr={1.5}
          item
          md={displayMode === "split" && stackDirection === "row" ? 12 : 5}
          sm={12}
        >
          <Typography textAlign={"left"} variant="body2" fontWeight={500} color="text.tier">
            {t("page:body.taskCount")}
          </Typography>
          <Typography textAlign={"right"} variant="body2" fontWeight={500}>
            {objectTypeData.count}
          </Typography>
        </Grid>
      </Grid>
      <Divider sx={{ m: 1, marginBottom: isLast ? 0 : 1 }} orientation="horizontal" />
    </Box>
  );

  return (
    <Stack mt={0.5} width={"100%"} direction={"column"} height={"calc(100vh - 88px)"}>
      <Stack
        sx={(theme) => ({
          height: "64px",
          overflowY: "hidden",
          overflowX: "scroll",
          ...theme.helpers(theme).scrollBar,
        })}
        width={"100%"}
        direction={"row"}
        justifyContent={"space-between"}
        spacing={2}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack mr={2} position={"relative"} direction={"row"}>
            <Autocomplete
              sx={{ width: "225px", marginTop: 1, marginRight: 2 }}
              disableClearable
              value={workflowType}
              onChange={(e, v) => {
                setWorkflowType(v as WorkflowType);
              }}
              options={Object.values(WorkflowType)}
              renderInput={(params) => (
                <OutlinedInput
                  label={t("field:label.chooseWorkflowType")}
                  {...params.InputProps}
                  inputProps={params.inputProps}
                  required
                  sx={{ width: "100%" }}
                  placeholder={t("field:placeholder.selectAnyResource")}
                />
              )}
            />

            <Stack position={"relative"} bottom={16} direction={"row"} spacing={2}>
              <DesktopDatePicker
                format={"YYYY-MM-DD"}
                sx={{ width: "175px" }}
                value={dayjs(startDate)}
                maxDate={dayjs(endDate)}
                onChange={(v) => {
                  v && setStartDate(moment(v.toString()).format("YYYY-MM-DD"));
                }}
              />

              <DesktopDatePicker
                format={"YYYY-MM-DD"}
                sx={{ width: "175px" }}
                minDate={dayjs(startDate)}
                value={dayjs(endDate)}
                onChange={(v) => {
                  v && setEndDate(moment(v.toString()).format("YYYY-MM-DD"));
                }}
              />
            </Stack>
          </Stack>
        </LocalizationProvider>
        <Stack
          height={"100%"}
          justifyContent={"flex-end"}
          alignContent={"center"}
          alignItems={"center"}
          direction={"row"}
          spacing={2}
        >
          <ToggleButtonGroup
            value={stackDirection}
            onChange={(e, v) => setStackDirection(v)}
            exclusive
            sx={{ opacity: displayMode === "graph" ? 0.3 : 1 }}
            aria-label="Stack direction"
            disabled={displayMode === "graph"}
          >
            <ToggleButton value="column" aria-label="Grid view">
              <TableRowsIcon />
            </ToggleButton>
            <ToggleButton value="row" aria-label="List view">
              <ViewColumnIcon />
            </ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            value={displayMode}
            onChange={(e, v) => setDisplayMode(v)}
            exclusive
            aria-label="Display mode"
          >
            <ToggleButton value="table" aria-label="Table view">
              <TocIcon />
            </ToggleButton>
            <ToggleButton value="graph" aria-label="Graph view">
              <EqualizerIcon />
            </ToggleButton>
            <ToggleButton value="split" aria-label="Split view">
              {stackDirection === "column" ? <HorizontalSplitIcon /> : <VerticalSplitIcon />}
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>
      <Stack spacing={1} direction={stackDirection} width={"100%"} height={"calc(100% - 64px)"}>
        {displayMode !== "graph" && (
          <Stack
            sx={{
              maxWidth: stackDirection === "row" ? (displayMode === "split" ? "230px" : "100%") : "100%",
            }}
            spacing={1}
            flexDirection={stackDirection === "column" ? "row" : "column"}
            flex={1}
            maxHeight={displayMode === "table" ? "100%" : stackDirection === "row" ? "100%" : "200px"}
          >
            <Box
              sx={{ backgroundColor: theme.palette.background.paper }}
              p={1}
              mr={1}
              textAlign={"center"}
              display={"flex"}
              justifyContent={"center"}
              width={stackDirection === "row" ? "100%" : "65%"}
              maxHeight={stackDirection === "row" ? "30%" : "100%"}
              flex={1}
            >
              {isLoading ? (
                <TableSkeleton />
              ) : (
                <KpiWorkflowTable
                  minimize={stackDirection === "row" && displayMode === "split"}
                  rows={kpiData}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              )}
            </Box>

            <Box
              sx={(theme) => ({
                backgroundColor: theme.palette.background.paper,
                height: stackDirection === "row" ? "-webkit-fill-available" : "100%",
                width: stackDirection === "row" ? "100%" : "35%",
                marginTop: stackDirection === "column" ? "0px !important" : "auto",
                ...theme.helpers(theme).scrollBar,
              })}
              pl={1}
              pt={1}
              pb={1}
              display={"flex"}
              flexDirection={"column"}
              overflow={"scroll"}
              maxHeight={stackDirection === "row" ? "70%" : "100%"}
            >
              {isLoading ? (
                <Box flexDirection={"column"} display={"flex"} width={"100%"} height={"100%"}>
                  <Skeleton />
                </Box>
              ) : parsedUserData && parsedUserData.length > 0 ? (
                parsedUserData.map((x, i) => generateDataBlock(x, i === parsedUserData.length - 1, i))
              ) : (
                <Box
                  height={"100%"}
                  width={"100%"}
                  display={"flex"}
                  justifyContent={"center"}
                  alignItems={"center"}
                  alignContent={"center"}
                  flexDirection={"column"}
                  textAlign={"center"}
                >
                  <Typography variant="h6">{t("page:body.nothingToShow")}</Typography>
                  <Typography color="text.tier" variant="body2">
                    {t("page:body.userHasNotData")}
                  </Typography>
                </Box>
              )}
            </Box>
          </Stack>
        )}

        {displayMode !== "table" && (
          <Box sx={{ backgroundColor: theme.palette.background.paper, height: "100%" }} flex={2}>
            {isLoading ? (
              <Box flexDirection={"column"} p={2} display={"flex"} width={"100%"} height={"100%"}>
                <Skeleton />
                <Skeleton />
                <Skeleton />
              </Box>
            ) : (
              <KpiCharts
                currentUserData={currentUserData}
                currentUserIndex={currentUserIndex}
                stackDirection={stackDirection}
                startDate={startDate}
                endDate={endDate}
                data={kpiData}
                selectedUserId={selectedRow}
                setSelectedUserId={setSelectedRow}
              />
            )}
          </Box>
        )}
      </Stack>
    </Stack>
  );
}

export default KpiWorkflow;
